import { useEffect, useRef, useState } from 'react';
import { QuickstartTheme } from '../../types/theme';
import { resolveCampaignStageImage } from '../../utils/campaignMedia';
import { letterboxContentFraction, resolveLetterboxCrop } from '../../utils/resolveLetterboxCrop';
import { collectStageVideoUrls } from '../../utils/resolveStageVideos';
import { playMutedInlineVideo } from './playMutedInlineVideo';

interface FallbackStageMediaProps {
  theme: QuickstartTheme;
  /**
   * Gaussian radius, as a CSS length rather than a number.
   *
   * A length so the caller decides whether it scales. Everything else on these screens is
   * a fraction of the stage, and a blur pinned to pixels quietly sharpens as the window
   * grows — at which point the campaign stops being a backdrop and starts competing with
   * the copy over it.
   */
  blur: string;
  /** Dimming applied over the media, 0-1. Higher on mobile where copy sits on top of it. */
  scrimOpacity?: number;
  /**
   * Which part of the frame survives the crop. Portrait tops the crop out so the game
   * kisses the top of the screen, since the bottom of the phone is opaque anyway.
   */
  anchor?: 'center' | 'top';
  /**
   * How far the frame is pushed past its box, to cover the soft edge the blur pulls in.
   * Layers that have to line up pixel for pixel — the desktop stage and the glass clipped
   * over it — must be given the same value.
   */
  zoom?: number;
  /**
   * `contain` keeps the full trailer in frame and pins unused space below (purple fade).
   * `cover` is the fallback-stage default.
   */
  fit?: 'cover' | 'contain';
  /**
   * 16:9 Payday trailers encode cinematic bars. Email-fallback crops them so black
   * cannot sit in the fade. Icon Drop opts out so PAYDAY matches STF framing.
   */
  cropLetterbox?: boolean;
  className?: string;
}

const DEFAULT_MEDIA_ZOOM = 1.06;

const toCssUrl = (url: string) => `url("${url.replace(/["\\]/g, '\\$&')}")`;

/**
 * Campaign media for the fallback stage.
 *
 * Distinct from SeamlessBackground, which is a fixed full-viewport layer driven by flow
 * phase. Here the media is scoped to whatever box the caller gives it — the left half of
 * the desktop split stage, or the full screen behind the mobile form.
 */
export const FallbackStageMedia = ({
  theme,
  blur,
  scrimOpacity = 0,
  anchor = 'center',
  zoom = DEFAULT_MEDIA_ZOOM,
  fit = 'cover',
  cropLetterbox = true,
  className,
}: FallbackStageMediaProps) => {
  const [failedVideoUrls, setFailedVideoUrls] = useState<string[]>([]);
  const [letterbox, setLetterbox] = useState({ top: 0, content: 1 });
  const videoRef = useRef<HTMLVideoElement>(null);

  const videoUrls = collectStageVideoUrls(theme);
  const mediaUrl = videoUrls.find((url) => !failedVideoUrls.includes(url));
  const fallbackImage = resolveCampaignStageImage(theme);

  const showVideo = !!mediaUrl;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) return;
    playMutedInlineVideo(video);
  }, [showVideo, mediaUrl]);

  const imageUrl = showVideo ? undefined : fallbackImage;

  const position = anchor === 'top' ? 'top center' : 'center';
  const transformOrigin = anchor === 'top' ? 'top center' : 'center';
  const contentFrac = letterbox.content;

  return (
    <div className={`absolute inset-0 overflow-hidden ${className ?? ''}`} aria-hidden='true'>
      {showVideo && (
        <video
          ref={videoRef}
          className='absolute left-0 w-full'
          src={mediaUrl}
          loop
          muted
          playsInline
          autoPlay
          controls={false}
          disablePictureInPicture
          preload='auto'
          data-overlay-video=''
          onLoadedData={(event) => playMutedInlineVideo(event.currentTarget)}
          onLoadedMetadata={(event) => {
            if (!cropLetterbox) return;
            const video = event.currentTarget;
            const crop = resolveLetterboxCrop(video.videoWidth, video.videoHeight);
            setLetterbox({ top: crop.top, content: letterboxContentFraction(crop) });
          }}
          onError={() => {
            setLetterbox({ top: 0, content: 1 });
            setFailedVideoUrls((failed) => (mediaUrl ? [...failed, mediaUrl] : failed));
          }}
          style={{
            top: letterbox.top === 0 ? 0 : `calc(-${letterbox.top} * 100% / ${contentFrac})`,
            height: contentFrac === 1 ? '100%' : `calc(100% / ${contentFrac})`,
            objectFit: fit,
            objectPosition: position,
            filter: `blur(${blur})`,
            transform: zoom === 1 ? undefined : `scale(${zoom})`,
            transformOrigin,
          }}
        />
      )}

      {!showVideo && imageUrl && (
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: toCssUrl(imageUrl),
            backgroundSize: fit,
            backgroundPosition: position,
            backgroundRepeat: 'no-repeat',
            filter: `blur(${blur})`,
            transform: zoom === 1 ? undefined : `scale(${zoom})`,
            transformOrigin,
          }}
        />
      )}

      {scrimOpacity > 0 && (
        <div className='absolute inset-0' style={{ backgroundColor: `rgba(0,0,0,${scrimOpacity})` }} />
      )}
    </div>
  );
};
